import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../../../api";
import s from "./ChatPanel.module.css";

const AVATAR_STYLE_PROMPT =
  "Create a beautifully stylized cartoon illustration with a modern, polished aesthetic. " +
  "Use rich but harmonious colors, smooth gradients, and clean linework with a slightly " +
  "cinematic feel \u2014 think Studio Ghibli meets Nordic design. Warm lighting, subtle depth, " +
  "and elegant composition. The style should feel sophisticated yet approachable, appealing " +
  "to young adults. " +
  "No text or letters in the image. Wide landscape composition (3:2 ratio) with an atmospheric background. " +
  "Keep important scene elements in the upper half of the image.";

const AVATAR_PROMPT_MAX = 1000;

function buildDefaultAvatarPrompt(story) {
  if (!story) return AVATAR_STYLE_PROMPT;
  const topic = story.topic?.in_swedish || "";
  const sentences = (story.sentences || [])
    .filter((s) => s.sequence_number > 0)
    .sort((a, b) => a.sequence_number - b.sequence_number)
    .slice(0, 3)
    .map((s) => s.in_swedish);
  const storyContent = [`Title: ${topic}`, ...sentences].join("\n");
  return `${AVATAR_STYLE_PROMPT}\n\nBased on the following story, illustrate the key scene:\n\n${storyContent}`;
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SPEAKERS = [
  { id: "a2RZfOPKpyNO38vDv3DD", name: "Annie", description: "Female · Swedish" },
  { id: "TIMFVcMCO4bdy7J79GWF", name: "Andres", description: "Male · Swedish" },
];

const CHAT_ACTIONS = [
  {
    key: "generate-story",
    label: "Generate Story",
    step: "01",
    primary: true,
    icon: "M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z",
  },
  {
    key: "generate-words-info",
    label: "Generate Words Info",
    step: "02",
    deletable: true,
    generatedFlag: "areWordsInfoGenerated",
    icon: "M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.1 18.1 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z M6 6h.008v.008H6V6Z",
  },
  {
    key: "generate-story-audio",
    label: "Generate Story Audio",
    step: "03",
    deletable: true,
    generatedFlag: "areAudioLessonGenerated",
    icon: "M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z",
  },
  {
    key: "generate-avatar",
    label: "Build Avatar",
    step: "04",
    deletable: true,
    generatedFlag: "hasAvatar",
    icon: "m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21ZM16.5 8.25a1.125 1.125 0 1 1-2.25 0 1.125 1.125 0 0 1 2.25 0Z",
  },
];

const SUPERTEXT_ACTIONS = [
  {
    key: "build-story",
    label: "Build Story",
    step: "01",
    primary: true,
    icon: "M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l5.653-4.655m5.014-2.024 3.15-3.15a2.318 2.318 0 0 0-3.276-3.276l-3.15 3.15m3.276 3.276L14.5 6.5",
  },
  {
    key: "generate-words-info",
    label: "Generate Words Info",
    step: "02",
    deletable: true,
    generatedFlag: "areWordsInfoGenerated",
    icon: "M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.1 18.1 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z M6 6h.008v.008H6V6Z",
  },
  {
    key: "generate-story-audio",
    label: "Generate Story Audio",
    step: "03",
    deletable: true,
    generatedFlag: "areAudioLessonGenerated",
    icon: "M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z",
  },
  {
    key: "generate-avatar",
    label: "Build Avatar",
    step: "04",
    deletable: true,
    generatedFlag: "hasAvatar",
    icon: "m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21ZM16.5 8.25a1.125 1.125 0 1 1-2.25 0 1.125 1.125 0 0 1 2.25 0Z",
  },
];

const MAX_WORDS = 350;

function countWords(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function getButtonState(action, { activeStory, areWordsInfoGenerated, areAudioLessonGenerated, hasAvatar }) {
  if (!activeStory) {
    return action.key === "generate-story" || action.key === "build-story";
  }
  switch (action.key) {
    case "generate-story":
    case "build-story":
      return true;
    case "generate-words-info":
      return !areWordsInfoGenerated;
    case "generate-story-audio":
      return !areAudioLessonGenerated;
    case "generate-avatar":
      return !hasAvatar;
    default:
      return false;
  }
}

function useDownloadAudioPlayer() {
  const audioRef = useRef(null);
  const [playingKey, setPlayingKey] = useState(null);

  const play = useCallback((key, url) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingKey === key) {
      setPlayingKey(null);
      return;
    }
    setPlayingKey(key);
    const audio = new Audio(url);
    audio.addEventListener("ended", () => setPlayingKey(null), { once: true });
    audio.addEventListener("error", () => { setPlayingKey(null); alert("Failed to play audio."); }, { once: true });
    audio.play().catch(() => setPlayingKey(null));
    audioRef.current = audio;
  }, [playingKey]);

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingKey(null);
  }, []);

  useEffect(() => {
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  return { play, stop, playingKey };
}

export default function ChatPanel({
  chatMode,
  onChatModeChange,
  userPrompt,
  onUserPromptChange,
  superTextPrompt,
  onSuperTextChange,
  onGenerateStory,
  onBuildStory,
  onGenerateWordsInfo,
  onDeleteWordsInfo,
  onGenerateStoryAudio,
  onDeleteStoryAudio,
  story,
  onGenerateAvatar,
  onDeleteAvatar,
  onNewStory,
  loadingAction,
  error,
  activeStory,
  areWordsInfoGenerated,
  areAudioLessonGenerated,
  hasAvatar,
  downloadAudioStatus,
  downloadAudioGenerating,
  onGenerateDownloadAudio,
}) {
  const { play: playDownloadAudio, playingKey } = useDownloadAudioPlayer();
  const isSuperText = chatMode === "supertext";
  const actions = isSuperText ? SUPERTEXT_ACTIONS : CHAT_ACTIONS;
  const currentPrompt = isSuperText ? superTextPrompt : userPrompt;
  const wordCount = countWords(currentPrompt);
  const overLimit = wordCount > MAX_WORDS;
  const loading = !!loadingAction;

  const flags = { areWordsInfoGenerated, areAudioLessonGenerated, hasAvatar };

  const [speakerModalOpen, setSpeakerModalOpen] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarPrompt, setAvatarPrompt] = useState("");
  const avatarTextareaRef = useRef(null);

  const openAvatarModal = useCallback(() => {
    setAvatarPrompt(buildDefaultAvatarPrompt(story));
    setAvatarModalOpen(true);
  }, [story]);

  const handleAvatarSubmit = useCallback(() => {
    if (!avatarPrompt.trim()) return;
    setAvatarModalOpen(false);
    onGenerateAvatar(avatarPrompt.trim());
  }, [avatarPrompt, onGenerateAvatar]);

  useEffect(() => {
    if (avatarModalOpen && avatarTextareaRef.current) {
      avatarTextareaRef.current.focus();
    }
  }, [avatarModalOpen]);

  useEffect(() => {
    if (!speakerModalOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setSpeakerModalOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [speakerModalOpen]);

  useEffect(() => {
    if (!avatarModalOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setAvatarModalOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [avatarModalOpen]);

  const handleClick = (action) => {
    if (action.key === "generate-story") {
      onGenerateStory();
    } else if (action.key === "build-story") {
      onBuildStory();
    } else if (action.key === "generate-words-info") {
      onGenerateWordsInfo();
    } else if (action.key === "generate-story-audio") {
      setSpeakerModalOpen(true);
    } else if (action.key === "generate-avatar") {
      openAvatarModal();
    }
  };

  const handleDelete = (action) => {
    if (action.key === "generate-words-info") {
      onDeleteWordsInfo();
    } else if (action.key === "generate-story-audio") {
      onDeleteStoryAudio();
    } else if (action.key === "generate-avatar") {
      onDeleteAvatar();
    }
  };

  const handleClear = () => {
    if (isSuperText) {
      onSuperTextChange("");
    } else {
      onUserPromptChange("");
    }
  };

  return (
    <div className={s.panel}>
      <div className={s.header}>
        <div className={s.headerIcon}>
          <svg className={s.headerIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
          </svg>
        </div>
        <h3 className={s.headerTitle}>Story Workshop</h3>
        {activeStory && (
          <button className={s.newStoryBtn} onClick={onNewStory} disabled={loading} title="Start new story">
            <svg className={s.newStoryIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
        )}
      </div>

      {/* Tab Switcher */}
      <div className={s.tabs}>
        <button
          className={`${s.tab} ${!isSuperText ? s.tabActive : ""}`}
          onClick={() => onChatModeChange("chat")}
        >
          <svg className={s.tabIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
          </svg>
          <span>Chat</span>
        </button>
        <button
          className={`${s.tab} ${isSuperText ? s.tabActive : ""}`}
          onClick={() => onChatModeChange("supertext")}
        >
          <svg className={s.tabIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
          </svg>
          <span>Super Text</span>
        </button>
      </div>

      <div className={s.content}>
        {/* Textarea with clear button */}
        <div className={s.textareaWrap}>
          <textarea
            className={`${s.contextInput} ${s.contextInputLarge}`}
            placeholder={isSuperText
              ? "Paste your Swedish text here (max 350 words)..."
              : "Describe your story context, theme, or vocabulary focus..."}
            rows={12}
            value={currentPrompt}
            onChange={(e) => isSuperText ? onSuperTextChange(e.target.value) : onUserPromptChange(e.target.value)}
            disabled={loading}
          />
          {currentPrompt && (
            <button
              className={s.clearBtn}
              onClick={handleClear}
              disabled={loading}
              title="Clear text"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Word count */}
        <span className={`${s.wordCount} ${overLimit ? s.wordCountOver : ""}`}>
          {wordCount} / {MAX_WORDS} words
        </span>

        {error && <p className={s.error}>{error}</p>}
        <div className={s.actions}>
          {actions.map((action) => {
            const enabled = getButtonState(action, { activeStory, areWordsInfoGenerated, areAudioLessonGenerated, hasAvatar });
            const isLoading = loadingAction === action.key;
            const disabled = !enabled || loading || ((action.key === "build-story" || action.key === "generate-story") && (overLimit || !currentPrompt.trim()));
            const showDelete = action.deletable && activeStory && flags[action.generatedFlag];

            return (
              <div key={action.key} className={s.actionRow}>
                <button
                  className={action.primary ? s.actionBtnPrimary : s.actionBtn}
                  onClick={() => handleClick(action)}
                  disabled={disabled}
                >
                  <svg className={s.actionBtnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d={action.icon} />
                  </svg>
                  <span className={s.actionBtnLabel}>
                    {isLoading ? "Generating..." : action.label}
                  </span>
                  <span className={s.stepIndicator}>{action.step}</span>
                </button>
                {showDelete && (
                  <button
                    className={s.deleteBtn}
                    onClick={() => handleDelete(action)}
                    disabled={loading}
                    title="Delete"
                  >
                    <svg className={s.deleteBtnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Download Audio Files Section */}
        {activeStory && areAudioLessonGenerated && (
          <div className={s.downloadSection}>
            <div className={s.downloadSectionHeader}>
              <svg className={s.downloadSectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12M12 16.5V3" />
              </svg>
              <span className={s.downloadSectionTitle}>Audio Lessons</span>
            </div>
            <div className={s.downloadList}>
              {[
                { key: "roleplay", label: "Role Play", endpoint: "roleplay-audio", suffix: "roleplay" },
              ].map((item) => {
                const statusValue = downloadAudioStatus?.[item.key];
                const isNotApplicable = statusValue === null;
                const exists = statusValue === true;
                const isGenerating = downloadAudioGenerating === item.key;
                const isPlaying = playingKey === item.key;
                const missingPrereq = isNotApplicable;

                const handlePlay = () => {
                  playDownloadAudio(item.key, `${BACKEND}/api/v1/admin/${item.endpoint}/${activeStory}`);
                };

                const handleDownloadFile = async () => {
                  try {
                    const res = await apiFetch(`/api/v1/admin/${item.endpoint}/${activeStory}`);
                    if (!res.ok) return;
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${item.suffix}.mp3`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  } catch { /* ignore */ }
                };

                return (
                  <div key={item.key} className={s.downloadRow}>
                    <div className={`${s.downloadStatus} ${exists ? s.downloadStatusDone : ""}`}>
                      {isGenerating ? (
                        <div className={s.downloadSpinner} />
                      ) : exists ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : (
                        <div className={s.downloadDot} />
                      )}
                    </div>
                    <span className={`${s.downloadLabel} ${exists ? s.downloadLabelDone : ""} ${missingPrereq ? s.downloadLabelDisabled : ""}`}>
                      {item.label}{isNotApplicable ? " — No unique words" : ""}
                    </span>
                    {exists && !isGenerating && (
                      <div className={s.downloadActions}>
                        <button
                          className={`${s.downloadActionBtn} ${isPlaying ? s.downloadActionBtnActive : ""}`}
                          onClick={handlePlay}
                          title={isPlaying ? "Stop" : `Play ${item.label}`}
                        >
                          {isPlaying ? (
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M5.25 7.5A2.25 2.25 0 0 1 7.5 5.25h2.25a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25H7.5a2.25 2.25 0 0 1-2.25-2.25v-9Zm9 0a2.25 2.25 0 0 1 2.25-2.25h2.25a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25H16.5a2.25 2.25 0 0 1-2.25-2.25v-9Z" />
                            </svg>
                          ) : (
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                            </svg>
                          )}
                        </button>
                        <button
                          className={s.downloadActionBtn}
                          onClick={handleDownloadFile}
                          title={`Download ${item.label}`}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12M12 16.5V3" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {!exists && !isGenerating && (
                      <button
                        className={s.downloadGenBtn}
                        onClick={() => onGenerateDownloadAudio(item.key)}
                        disabled={loading || missingPrereq}
                        title={isNotApplicable ? "No unique words" : missingPrereq ? "Prerequisites not met" : `Generate ${item.label}`}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 5v14m-7-7h14" />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>

      {speakerModalOpen && createPortal(
        <div className={s.avatarOverlay}>
          <div className={s.avatarBackdrop} onClick={() => setSpeakerModalOpen(false)} />
          <div className={s.speakerModal} onClick={(e) => e.stopPropagation()}>
            <div className={s.avatarModalHeader}>
              <svg className={s.avatarModalIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
              </svg>
              <h3 className={s.avatarModalTitle}>Select Speaker</h3>
              <button className={s.avatarModalClose} onClick={() => setSpeakerModalOpen(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className={s.speakerList}>
              {SPEAKERS.map((speaker) => (
                <button
                  key={speaker.id}
                  className={s.speakerOption}
                  onClick={() => { setSpeakerModalOpen(false); onGenerateStoryAudio(speaker.id); }}
                >
                  <div className={s.speakerAvatar}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                    </svg>
                  </div>
                  <div className={s.speakerInfo}>
                    <span className={s.speakerName}>{speaker.name}</span>
                    <span className={s.speakerDesc}>{speaker.description}</span>
                  </div>
                  <svg className={s.speakerArrow} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {avatarModalOpen && createPortal(
        <div className={s.avatarOverlay}>
          <div className={s.avatarBackdrop} onClick={() => setAvatarModalOpen(false)} />
          <div className={s.avatarModal} onClick={(e) => e.stopPropagation()}>
            <div className={s.avatarModalHeader}>
              <svg className={s.avatarModalIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21ZM16.5 8.25a1.125 1.125 0 1 1-2.25 0 1.125 1.125 0 0 1 2.25 0Z" />
              </svg>
              <h3 className={s.avatarModalTitle}>Build Avatar</h3>
              <button className={s.avatarModalClose} onClick={() => setAvatarModalOpen(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className={s.avatarModalBody}>
              <label className={s.avatarModalLabel}>Image Prompt</label>
              <textarea
                ref={avatarTextareaRef}
                className={s.avatarModalTextarea}
                value={avatarPrompt}
                onChange={(e) => {
                  if (e.target.value.length <= AVATAR_PROMPT_MAX) {
                    setAvatarPrompt(e.target.value);
                  }
                }}
                rows={10}
              />
              <div className={s.avatarModalFooter}>
                <span className={`${s.avatarCharCount} ${avatarPrompt.length >= AVATAR_PROMPT_MAX ? s.avatarCharCountOver : ""}`}>
                  {avatarPrompt.length} / {AVATAR_PROMPT_MAX}
                </span>
                <div className={s.avatarModalActions}>
                  <button className={s.avatarCancelBtn} onClick={() => setAvatarModalOpen(false)}>
                    Cancel
                  </button>
                  <button
                    className={s.avatarGenerateBtn}
                    onClick={handleAvatarSubmit}
                    disabled={!avatarPrompt.trim()}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
                    </svg>
                    Generate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
